import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Plus,
    Check,
    Flame,
    X,
} from 'lucide-react';
import { useAppState, Habit } from '../../contexts/AppContext';

const HABIT_COLORS = [
    '#0D5F6B', '#2A7A85', '#EF4444', '#F59E0B',
    '#10B981', '#8B5CF6', '#EC4899', '#3B82F6',
];

const HABIT_ICONS = ['💪', '📚', '🧘', '💧', '🏃', '🎯', '✍️', '🌅', '😴', '🥗'];

export default function AppHabits() {
    const navigate = useNavigate();
    const { habits, addHabit, toggleHabitComplete } = useAppState();

    const today = new Date().toISOString().split('T')[0];
    const [showAddModal, setShowAddModal] = useState(false);
    const [newHabitTitle, setNewHabitTitle] = useState('');
    const [newHabitIcon, setNewHabitIcon] = useState('💪');
    const [newHabitColor, setNewHabitColor] = useState(HABIT_COLORS[0]);
    const [newHabitFrequency, setNewHabitFrequency] = useState<Habit['frequency']>('daily');

    const handleAddHabit = () => {
        if (!newHabitTitle.trim()) return;

        addHabit({
            title: newHabitTitle.trim(),
            icon: newHabitIcon,
            color: newHabitColor,
            frequency: newHabitFrequency,
        });

        setNewHabitTitle('');
        setNewHabitIcon('💪');
        setNewHabitColor(HABIT_COLORS[0]);
        setNewHabitFrequency('daily');
        setShowAddModal(false);
    };

    const isCompletedToday = (habit: Habit) => habit.completedDates.includes(today);

    // Get last 7 days for the calendar view
    const last7Days = Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (6 - i));
        return date.toISOString().split('T')[0];
    });

    const getDayLabel = (dateStr: string) => {
        const date = new Date(dateStr + 'T00:00:00');
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (dateStr === today.toISOString().split('T')[0]) return 'Today';
        if (dateStr === yesterday.toISOString().split('T')[0]) return 'Yday';
        return date.toLocaleDateString('en-US', { weekday: 'short' });
    };

    // Calculate overall completion rate for today
    const todayCompletion = habits.length > 0
        ? Math.round((habits.filter(h => isCompletedToday(h)).length / habits.length) * 100)
        : 0;

    return (
        <div className="min-h-screen bg-[#F5F8FA] dark:bg-dark-bg pb-24">
            {/* Header */}
            <div className="bg-gradient-to-br from-[#0D5F6B] to-[#2A7A85] text-white px-4 pt-6 pb-8">
                <div className="flex items-center justify-between mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 -ml-2 hover:bg-white/10 rounded-full transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="p-2 hover:bg-white/10 rounded-full transition-colors"
                    >
                        <Plus className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                        <Flame className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">Daily Habits</h1>
                        <p className="text-white/80 text-sm">{habits.length} habits tracked</p>
                    </div>
                </div>

                {/* Today's Progress */}
                <div className="bg-white/10 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-white/80">Today's Progress</span>
                        <span className="font-bold">{todayCompletion}%</span>
                    </div>
                    <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-white rounded-full transition-all duration-300"
                            style={{ width: `${todayCompletion}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* Habits List */}
            <div className="p-4 -mt-4 space-y-3">
                {habits.length === 0 ? (
                    <div className="bg-white dark:bg-dark-card rounded-2xl p-8 text-center shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                        <div className="w-16 h-16 bg-[#D9EEF2] rounded-full flex items-center justify-center mx-auto mb-4">
                            <Flame className="w-8 h-8 text-[#0D5F6B]" />
                        </div>
                        <h3 className="font-semibold text-[#1A2832] dark:text-white mb-2">No habits yet</h3>
                        <p className="text-sm text-[#7B8C9B] mb-4">
                            Start tracking daily habits to build better routines
                        </p>
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="px-4 py-2 bg-[#0D5F6B] text-white rounded-xl font-medium"
                        >
                            Add First Habit
                        </button>
                    </div>
                ) : (
                    habits.map(habit => (
                        <div
                            key={habit.id}
                            className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08)]"
                        >
                            <div className="flex items-center gap-3 mb-3">
                                <div
                                    className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
                                    style={{ backgroundColor: `${habit.color}20` }}
                                >
                                    {habit.icon}
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-[#1A2832] dark:text-white">{habit.title}</h3>
                                    <div className="flex items-center gap-2 text-sm text-[#7B8C9B]">
                                        <Flame className="w-3 h-3 text-orange-500" />
                                        <span>{habit.currentStreak} day streak</span>
                                        {habit.longestStreak > 0 && (
                                            <span className="text-xs">• Best: {habit.longestStreak}</span>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={() => toggleHabitComplete(habit.id, today)}
                                    className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${isCompletedToday(habit)
                                        ? 'bg-green-500 text-white'
                                        : 'border-2 border-[#DCE4EA] text-[#DCE4EA]'
                                        }`}
                                >
                                    <Check className="w-5 h-5" />
                                </button>
                            </div>

                            {/* 7-day calendar */}
                            <div className="flex justify-between">
                                {last7Days.map(date => {
                                    const isCompleted = habit.completedDates.includes(date);
                                    const isToday = date === today;
                                    return (
                                        <button
                                            key={date}
                                            onClick={() => toggleHabitComplete(habit.id, date)}
                                            className="flex flex-col items-center gap-1"
                                        >
                                            <span className={`text-xs ${isToday ? 'font-bold text-[#0D5F6B]' : 'text-[#7B8C9B]'}`}>
                                                {getDayLabel(date)}
                                            </span>
                                            <div
                                                className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${isCompleted
                                                    ? ''
                                                    : 'bg-gray-100'
                                                    }`}
                                                style={isCompleted ? { backgroundColor: habit.color } : {}}
                                            >
                                                {isCompleted && <Check className="w-4 h-4 text-white" />}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Add Habit Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 flex items-end z-50">
                    <div className="bg-white dark:bg-dark-card w-full rounded-t-3xl p-6 max-h-[80vh] overflow-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-[#1A2832]">New Habit</h2>
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-full"
                            >
                                <X className="w-5 h-5 text-[#7B8C9B]" />
                            </button>
                        </div>

                        {/* Habit Title */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-[#7B8C9B] mb-2">
                                Habit Name
                            </label>
                            <input
                                type="text"
                                value={newHabitTitle}
                                onChange={(e) => setNewHabitTitle(e.target.value)}
                                placeholder="e.g., Morning meditation"
                                className="w-full px-4 py-3 border border-[#DCE4EA] rounded-xl outline-none focus:border-[#0D5F6B]"
                            />
                        </div>

                        {/* Icon Selection */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-[#7B8C9B] mb-2">
                                Icon
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {HABIT_ICONS.map(icon => (
                                    <button
                                        key={icon}
                                        onClick={() => setNewHabitIcon(icon)}
                                        className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${newHabitIcon === icon
                                            ? 'bg-[#0D5F6B] ring-2 ring-offset-2 ring-[#0D5F6B]'
                                            : 'bg-gray-100'
                                            }`}
                                    >
                                        {icon}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Color Selection */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-[#7B8C9B] mb-2">
                                Color
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {HABIT_COLORS.map(color => (
                                    <button
                                        key={color}
                                        onClick={() => setNewHabitColor(color)}
                                        className={`w-8 h-8 rounded-full ${newHabitColor === color
                                            ? 'ring-2 ring-offset-2 ring-gray-400'
                                            : ''
                                            }`}
                                        style={{ backgroundColor: color }}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Frequency */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-[#7B8C9B] mb-2">
                                Frequency
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                {(['daily', 'weekdays', 'weekly'] as const).map(freq => (
                                    <button
                                        key={freq}
                                        onClick={() => setNewHabitFrequency(freq)}
                                        className={`p-3 rounded-xl border-2 capitalize ${newHabitFrequency === freq
                                            ? 'border-[#0D5F6B] bg-[#D9EEF2] text-[#0D5F6B]'
                                            : 'border-[#DCE4EA] text-[#7B8C9B]'
                                            }`}
                                    >
                                        {freq}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Add Button */}
                        <button
                            onClick={handleAddHabit}
                            disabled={!newHabitTitle.trim()}
                            className="w-full py-4 bg-[#0D5F6B] text-white rounded-xl font-semibold disabled:opacity-50"
                        >
                            Add Habit
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
